import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import Dialog from '@mui/material/Dialog';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import DialogContent from '@mui/material/DialogContent';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';

import Checkbox from 'src/components/input/checkbox/Checkbox';
import Auth from 'src/services/api/auth/Auth';
import { redirectToLandlordLogin } from 'src/api';
import type { ILead } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import {
    type ICreateShowingRequest,
    useCreateShowing,
    useProperties,
    useUpdateShowing,
    useDeleteShowing
} from 'src/services/api';
import { useUpdateLead } from 'src/services/api/leads/use-update-lead';
import { useTeamMembers } from 'src/services/api/team/members/use-team-members';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { BlackButtonWhiteText } from 'src/common/Buttons';
import { FlexColumn } from 'src/components/flex';
import { Transition } from 'src/components/ModalTransition';
import ReffieLoadingButton from 'src/components/reffie-loading-button/ReffieLoadingButton';

import modalButtonStyles from 'src/common/ModalButton.module.css';
import styles from 'src/components/create-or-update-tour-modal/CreateOrUpdateTourModal.module.css';

interface Props {
    open: boolean;
    onClose: () => void;
    /**
     * The lead that is initially selected
     * Do not pass anything (undefined) if none are selected
     */
    selectedLeadUuid?: string;
    /**
     * Necessary to edit the showing
     */
    landlordUserId?: number;
    /**
     * Array of leads to choose from
     * Necessary for the user to be able to switch between leads
     * If you do not want the user to switch between leads, simply pass a single lead
     */
    leads: ILead[];
    /**
     * Houses both the date and time
     */
    initDateTime?: string;
    initPropertyId: number | null;
    /**
     * If this is an existing showing, this will be the ID of the showing
     */
    showingId?: number;
    /**
     * If this showing was created from message contents, this is the UUID of the message
     */
    sourceMessageUuid?: string;
    onCreated?: (showing: ICreateShowingRequest) => void;
    isSelfShow?: boolean;
}

export default function CreateOrUpdateTourModal(props: Props) {
    if (!Auth.accessToken) {
        console.error('Failed to load access token from React context');
        redirectToLandlordLogin('/landlord/inner/rental-applications');
        return <></>;
    }

    const { data: properties } = useProperties();
    const { data: teamMembers } = useTeamMembers();
    const { loggedInUser } = useAuthenticatedState();

    const initSelectedLead = props.leads.find((lead) => lead.uuid === props.selectedLeadUuid) || null;
    const initDateVal = props.initDateTime ? dayjs(props.initDateTime) : null;

    const [lead, setLead] = useState<{ value: string, label: string | null } | null>(
        initSelectedLead ? { value: initSelectedLead.uuid, label: initSelectedLead.name } : null
    );
    const [isSelfShow, setIsSelfShow] = useState<boolean>(props.isSelfShow || false);
    const [property, setProperty] = useState<{ value: number, label: string } | null>();
    const [date, setDate] = useState<dayjs.Dayjs | null>(initDateVal);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const { mutateAsync: createShowing, isLoading: isCreateLoading } = useCreateShowing();
    const { mutateAsync: updateShowing, isLoading: isUpdateLoading } = useUpdateShowing();
    const { mutateAsync: deleteShowing, isLoading: isDeleteLoading } = useDeleteShowing();
    const { mutateAsync: updateLead, isLoading: isTransferring } = useUpdateLead(lead?.value ?? '');

    const isSaveOrUpdateLoading = isCreateLoading || isUpdateLoading;

    const selectedLead = props.leads.find(l => l.uuid === lead?.value) ?? null;
    const ownerName = teamMembers?.find(m => m.id === selectedLead?.owner)?.name ?? 'another team member';
    const isOwner = !selectedLead || selectedLead.owner === loggedInUser.id;

    const handleCreate = async () => {
        if (!lead) {
            setErrorMessage('please select a lead');
            return;
        }
        if (!property) {
            setErrorMessage('please select a property');
            return;
        }
        if (!date) {
            setErrorMessage('please select a date');
            return;
        }
        const payload: ICreateShowingRequest = {
            property_id: property?.value,
            lead_uuid: lead.value,
            start_time: date.format(),
            source_message_uuid: props.sourceMessageUuid || null,
            is_self_show: isSelfShow,
        };
        await createShowing(payload);
        props.onClose();
        props?.onCreated?.(payload);
    };

    const handleTransferOwnership = async () => {
        if (!loggedInUser) { return; }
        await updateLead({ owner: loggedInUser.id });
    };

    const handleEdit = async () => {
        if (!property) {
            setErrorMessage('please select a property');
            return;
        }
        if (!date) {
            setErrorMessage('please select a date');
            return;
        }

        const updatedShowing = {
            id: props.showingId,
            landlord_user_id: props.landlordUserId,
            property_id: property.value,
            lead_uuid: lead?.value,
            start_time: date.format(),
            source_message_uuid: props.sourceMessageUuid || null,
            is_self_show: isSelfShow,
        } as IShowing;

        await updateShowing(updatedShowing);
        props.onClose();
    };

    const handleSave = async () => {
        if (props.showingId) {
            await handleEdit();
        } else {
            await handleCreate();
        }
    };

    const handleDelete = async () => {
        if (!props.showingId) {
            throw new Error('props.showingId is null');
        }
        await deleteShowing(props.showingId);
        props.onClose();
    };

    const leadOptions = useMemo(() => {
        return props.leads.map((o) => {
            if (!o.name) {
                return {
                    value: o.uuid,
                    label: 'Unknown Lead',
                };
            }
            return {
                value: o.uuid,
                label: o.name,
            };
        });
    }, [props.leads]);

    const propertyOptions = useMemo(() => {
        if (!properties) {
            return [];
        }

        return properties?.map((o) => ({
            value: o.id,
            label: o.name,
        }));
    }, [properties]);

    useEffect(() => {
        const leadProperty = propertyOptions?.find((o) => o.value === props.initPropertyId);
        setProperty(prev => {
            if (prev === undefined) {
                return leadProperty;
            } else {
                return prev;
            }
        });
    }, [props.leads, propertyOptions, props.initPropertyId]);

    return (
        <>
            <Dialog open={props.open} onClose={props.onClose} maxWidth="sm"
                TransitionComponent={Transition}
            >
                <DialogTitle>{props.showingId ? 'Edit Tour' : 'Create a New Tour'}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 1 }}>
                    You can save tour dates in Reffie to help you keep track of where your tours are.
                    </DialogContentText>

                    {errorMessage && <Alert severity="warning" className={styles.modalAlert}>{errorMessage}</Alert>}

                    {!isOwner && (
                        <Alert
                            severity="warning"
                            className={styles.modalAlert}
                            action={
                                <ReffieLoadingButton
                                    variant="outlined"
                                    color="warning"
                                    size="small"
                                    loading={isTransferring}
                                    onClick={handleTransferOwnership}
                                >
                                    Transfer
                                </ReffieLoadingButton>
                            }
                        >
                            {`You do not own this lead. Transfer ownership of the lead from ${ownerName} to yourself before scheduling a tour.`}
                        </Alert>
                    )}

                    <form>
                        <FlexColumn>
                            <div>
                                <FormControl fullWidth>
                                    {initSelectedLead ?
                                        <TextField variant="filled"
                                            fullWidth
                                            label="Lead Name"
                                            value={initSelectedLead.name || ''}
                                            aria-readonly
                                            inputProps={{ readOnly: true }}
                                            data-testid="lead-name-field"
                                        /> :
                                        <Autocomplete
                                            options={leadOptions}
                                            value={lead}
                                            onChange={(_event, newValue) => setLead(newValue)}
                                            renderInput={(params) => <TextField {...params} label="Lead" />}
                                            data-testid="lead-select"
                                        />
                                    }
                                </FormControl>
                            </div>
                            <div>
                                <FormControl fullWidth>
                                    <Autocomplete
                                        options={propertyOptions}
                                        value={property || null}
                                        onChange={(_event, newValue) => setProperty(newValue)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Property"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    style: { overflow: 'hidden' }
                                                }}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                                                }}
                                            />
                                        )}
                                        renderOption={(_props, option) => (
                                            <li {..._props} key={option.value}>
                                                <span className="ellipsisText" style={{ display: 'block', width: '100%' }}>
                                                    {option.label}
                                                </span>
                                            </li>
                                        )}
                                        data-testid="property-select"
                                    />
                                </FormControl>
                            </div>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <div data-testid="date-select">
                                    <DateTimePicker
                                        label="Date"
                                        value={date}
                                        onChange={(newDate) => setDate(newDate)}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                error: false,
                                            },
                                        }}
                                        disablePast
                                        closeOnSelect={false}
                                    />
                                </div>
                            </LocalizationProvider>
                            <div>
                                <FormControl fullWidth>
                                    <Checkbox
                                        value={isSelfShow}
                                        labelPlacement="start"
                                        label="Self show"
                                        onChange={setIsSelfShow}
                                    />
                                </FormControl>
                            </div>
                        </FlexColumn>
                    </form>
                </DialogContent>
                <DialogActions>
                    <BlackButtonWhiteText
                        className={modalButtonStyles.modalButton}
                        onClick={props.onClose}
                        data-testid="cancel-tour-button"
                    >Cancel</BlackButtonWhiteText>
                    {props.showingId &&
                    <ReffieLoadingButton
                        color="error"
                        disabled={isSaveOrUpdateLoading}
                        loading={isDeleteLoading}
                        style={isDeleteLoading ? { paddingLeft: '32px' } : {}}
                        onClick={handleDelete}
                        data-testid="delete-tour-button"
                    >Delete</ReffieLoadingButton>
                    }
                    <ReffieLoadingButton
                        color="success"
                        disabled={isDeleteLoading || !isOwner || !lead || !property || !date}
                        loading={isSaveOrUpdateLoading}
                        style={isDeleteLoading ? { paddingLeft: '32px' } : {}}
                        onClick={handleSave}
                        data-testid="save-tour-button"
                    >{props.showingId ? 'Update' : 'Create'}</ReffieLoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
}
